import React, { useState } from "react";

import classNames from "classnames";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "../../shared/hooks/useNavigation";
import PropTypes from "prop-types";
import { Container, Navbar } from "react-bootstrap";
import styled from "styled-components";

import { useStores } from "../../contexts/StoreContext";
import useDevice from "../../shared/customHooks/useDevice";
import Markdown from "../../shared/styles/Markdown";
import { horizontalAlignmentMap } from "../../shared/styles/positionMaps";
import AdditionalTopNavigation from "./AdditionalTopNavigation";
import CenteredNavLinks from "./CenteredNavLinks";
import CountryCodeNavigation from "./CountryCodeNavigation";
import LeftHandSideNavigation from "./LeftHandSideNavigation";
import NavLinks from "./NavLinks";
import NavbarActions from "./NavbarActions";
import VerticalDropdownNavigation from "./VerticalDropdownNavigation";

const StyledNavbarContainer = styled.header`
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.$isOverlayingPage
      ? `
      position: absolute;
      width: 100%;
      `
      : ""}

  ${(props) => props.$dynamicStyles || ""}
`;

const StyledNavbar = styled(Navbar)`
  background: ${({ $background, theme }) =>
    `${$background || theme.primaryColor}`};
  z-index: 12;
  opacity: ${({ $isOpaque, $opacity }) =>
    $isOpaque ? $opacity || 1 : "unset"};
  min-height: ${({ $logoHeight }) => `${$logoHeight || 50}px`};

  ${({ $isFullWidth }) =>
    !$isFullWidth
      ? `
      max-width: 1300px;
      margin: auto;
      width: 100%;
      `
      : ""}
`;

const StyledNavigationWrapper = styled.div`
  height: ${({ $logoDimensions }) => `${$logoDimensions?.height || 50}px`};
  max-height: 200px;
  width: 100%;
`;

const StyledHeader = styled.div`
  height: ${({ $logoDimensions }) => `${$logoDimensions?.height || 50}px`};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
`;

const StyledBrand = styled(Navbar.Brand)`
  max-width: 50%;
  width: ${({ $logoDimensions }) => `${$logoDimensions.width || 200}px`};
  height: ${({ $logoDimensions }) => `${$logoDimensions.height || 50}px`};
  max-height: 200px;
  position: ${({ $centerIcon }) => ($centerIcon ? "absolute" : "relative")};
  left: ${({ $centerIcon, $logoDimensions }) =>
    $centerIcon
      ? `calc(50% - ${
          ($logoDimensions.width && $logoDimensions.width / 2) || 100
        }px)`
      : "unset"};
`;

const StyledLogo = styled(Navbar.Brand)`
  width: ${({ $logoDimensions }) => `${$logoDimensions.width || 200}px`};
  height: ${({ $logoDimensions }) => `${$logoDimensions.height || 50}px`};
  max-height: 200px;
  position: ${({ $centerIcon }) => ($centerIcon ? "absolute" : "relative")};
  left: ${({ $centerIcon, $logoDimensions }) =>
    $centerIcon
      ? `calc(50% - ${
          ($logoDimensions.width && $logoDimensions.width / 2) || 100
        }px)`
      : "unset"};
`;

const StyledLinksWrapper = styled.div`
  width: ${({ $logoDimensions }) => `${$logoDimensions.width || 200}px`};
  height: ${({ $logoDimensions }) => `${$logoDimensions.height || 50}px`};
  padding-left: 10px;
`;

function TopNavigation(props) {
  const { settings, customLogo, backgroundColour } = props;
  const { configStore, navigationStore } = useStores();
  const { config } = configStore;
  const {
    topNavigation,
    isAdditionalHeaderNavigationUsed,
    isCountryCodeNavigationUsed,
  } = navigationStore;
  const { isMobile, isSmallScreen } = useDevice();
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);
  const isCustomDesktopNavbarUsed = !isEmpty(settings?.customDesktopNavbar);

  if (isCustomDesktopNavbarUsed && !isMobile && !settings.isAlwaysMobileView) {
    return <Markdown>{settings.customDesktopNavbar}</Markdown>;
  }

  let logoUrl = config?.logo?.url;
  let logoHeight = config?.logoHeight;
  let logoWidth = config?.logoWidth;

  if (customLogo?.url) {
    logoUrl = customLogo?.url;
    logoHeight = customLogo?.height;
    logoWidth = customLogo?.width;
  }

  if (settings?.type === "menuUnderneathLogo") {
    return (
      <VerticalDropdownNavigation
        settings={settings}
        customLogo={customLogo}
        backgroundColour={backgroundColour}
      />
    );
  }

  if (settings?.type === "leftHandSide") {
    return (
      <LeftHandSideNavigation settings={settings} customLogo={customLogo} />
    );
  }

  if (settings?.type === "centeredLinks") {
    return (
      <StyledNavbarContainer
        $dynamicStyles={settings?.CSS}
        id="top-navigation"
        $isOverlayingPage={settings?.isOverlayingPage}
      >
        <StyledNavbar
          $background={backgroundColour || settings?.background}
          $isOpaque={settings?.isOpaque || false}
          $opacity={settings?.opacity || 0}
          style={settings?.styles}
          $isFullWidth={settings?.fullWidth}
          $logoHeight={logoHeight}
          expanded={expanded}
          onToggle={() => setExpanded(!expanded)}
        >
          <StyledHeader
            $isMobile={isMobile || isSmallScreen || settings.isAlwaysMobileView}
            className="d-flex align-items-center justify-content-between w-100"
            id="navbar-actions-wrapper"
            $logoDimensions={{
              height: logoHeight,
              width: logoWidth,
            }}
          >
            {logoUrl && (
              <StyledLogo
                $centerIcon={isMobile || isSmallScreen || settings.isAlwaysMobileView}
                className="header-logo"
                $logoDimensions={{
                  height: logoHeight,
                  width: logoWidth,
                }}
              >
                <Link href={config.logoUrl || "/"}>
                  <Image
                    alt={config.siteName}
                    layout="fill"
                    objectFit="contain"
                    priority
                    src={logoUrl}
                  />
                </Link>
              </StyledLogo>
            )}
            <CenteredNavLinks
              className="h-100"
              isAlwaysMobileView={settings.isAlwaysMobileView}
              navigationBackgroundColor={
                backgroundColour || settings?.background
              }
              onLinkClick={() => setExpanded(false)}
            />
            {isCountryCodeNavigationUsed && (
              <StyledLinksWrapper
                $logoDimensions={{
                  height: logoHeight,
                  width: logoWidth,
                }}
              >
                <CountryCodeNavigation />
              </StyledLinksWrapper>
            )}
          </StyledHeader>
        </StyledNavbar>
      </StyledNavbarContainer>
    );
  }

  const navbarClassname = classNames(
    "px-4 position-relative",
    `justify-content-${horizontalAlignmentMap[config?.logoAlignment || "left"]}`
  );

  const isJobPage = router?.pathname?.includes("jobSlug");

  const navbarPosition = {
    ...(!isJobPage && settings?.position && { [settings?.position]: "top" }),
  };

  const showNavbarActions =
    !isEmpty(settings?.navbarText) ||
    settings?.showLocaleSelector ||
    !isEmpty(topNavigation);

  const NavLinksClassname = classNames("h-100", {
    "w-100": !settings?.showSearch,
  });

  return (
    <StyledNavbarContainer
      $dynamicStyles={settings?.CSS}
      id="top-navigation"
      $isOverlayingPage={settings?.isOverlayingPage}
    >
      {isAdditionalHeaderNavigationUsed && (
        <AdditionalTopNavigation style={settings?.additionalHeaderStyles} />
      )}
      <StyledNavbar
        $background={backgroundColour || settings?.background}
        $isOpaque={settings?.isOpaque || false}
        $opacity={settings?.opacity || 0}
        $isFullWidth={settings?.fullWidth}
        $logoHeight={logoHeight}
        collapseOnSelect
        expand="lg"
        style={settings?.styles}
        {...navbarPosition}
      >
        <Container className={navbarClassname} fluid>
          {logoUrl && (
            <StyledBrand
              $centerIcon={isMobile || isSmallScreen || settings.isAlwaysMobileView}
              $logoDimensions={{
                height: logoHeight,
                width: logoWidth,
              }}
              className="me-0 header-logo"
            >
              <Link href={config.logoUrl || "/"}>
                <Image
                  alt={config.siteName}
                  layout="fill"
                  objectFit="contain"
                  priority
                  src={logoUrl}
                />
              </Link>
            </StyledBrand>
          )}
          {showNavbarActions && (
            <StyledNavigationWrapper
              $isMobile={isMobile || isSmallScreen || settings.isAlwaysMobileView}
              $logoDimensions={{
                height: logoHeight,
                width: logoWidth,
              }}
              className="d-flex align-items-center justify-content-between"
              id="navbar-actions-wrapper"
            >
              <NavLinks
                className={NavLinksClassname}
                navigationBackgroundColor={
                  backgroundColour || settings?.background
                }
              />
              <NavbarActions
                navbarText={settings?.navbarText}
                navbarTextColor={settings?.navbarTextColor}
                searchBarButtonText={settings?.searchBarButtonText}
                searchBarKeywordsText={settings?.searchBarKeywordsText}
                searchBarLocationText={settings?.searchBarLocationText}
                showLocaleSelector={settings?.showLocaleSelector}
                showSearch={settings?.showSearch}
                socialMediaSettings={settings?.socialMediaNavigation}
              />
            </StyledNavigationWrapper>
          )}
        </Container>
      </StyledNavbar>
    </StyledNavbarContainer>
  );
}

TopNavigation.defaultProps = {
  customLogo: null,
  pages: null,
  settings: null,
};

TopNavigation.propTypes = {
  customLogo: PropTypes.shape({}),
  pages: PropTypes.arrayOf(PropTypes.shape()),
  settings: PropTypes.shape({
    CSS: PropTypes.string,
    additionalHeaderStyles: PropTypes.shape({}),
    background: PropTypes.string,
    backgroundColor: PropTypes.string,
    customDesktopNavbar: PropTypes.string,
    isOpaque: PropTypes.bool,
    navbarText: PropTypes.string,
    navbarTextColor: PropTypes.string,
    opacity: PropTypes.number,
    position: PropTypes.string,
    searchBarButtonText: PropTypes.string,
    searchBarKeywordsText: PropTypes.string,
    searchBarLocationText: PropTypes.string,
    showLocaleSelector: PropTypes.bool,
    showSearch: PropTypes.bool,
    socialMediaNavigation: PropTypes.shape({}),
    styles: PropTypes.shape({}),
  }),
};

export default observer(TopNavigation);
